import { useId } from "react";

import { cn } from "@/lib/utils";

type ControlExchangeLogoProps = {
  animated?: boolean;
  className?: string;
  decorative?: boolean;
  layout?: "horizontal" | "stacked";
  markClassName?: string;
  markOnly?: boolean;
  tone?: "default" | "reverse" | "monochrome";
  wordmarkClassName?: string;
};

const PARALLELOGRAM_PATH =
  "M76 4H123C127.8 4 130 7.4 128.9 12L116.1 68C115 73.2 112.4 76 107 76H60C55.2 76 53 72.6 54.1 68L66.9 12C68 6.8 70.6 4 76 4Z";

export default function ControlExchangeLogo({
  animated = false,
  className,
  decorative = false,
  layout = "horizontal",
  markClassName,
  markOnly = false,
  tone = "default",
  wordmarkClassName,
}: ControlExchangeLogoProps) {
  const id = useId().replace(/:/g, "");
  const clipId = `control-exchange-circle-${id}`;
  const maskId = `control-exchange-cutout-${id}`;
  const animatedCircleMaskId = `control-exchange-animated-circle-${id}`;
  const animatedPanelMaskId = `control-exchange-animated-panel-${id}`;

  const circleClassName = tone === "default" ? "fill-[#005BFF]" : "fill-current";
  const parallelogramClassName = tone === "default" ? "fill-[#1F252E]" : "fill-current";
  const toneClassName = tone === "reverse" ? "text-white" : tone === "monochrome" ? "text-current" : "text-[#1F252E]";

  return (
    <span
      className={cn(
        "inline-flex shrink-0",
        layout === "stacked" ? "flex-col items-center gap-2" : "items-center gap-2.5",
        toneClassName,
        className,
      )}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : "Control Exchange"}
      role={decorative ? undefined : "img"}
    >
      <svg
        viewBox="0 0 132 80"
        aria-hidden="true"
        className={cn("h-8 w-auto shrink-0", animated && "overflow-visible", markClassName)}
      >
        <defs>
          {animated ? (
            <>
              <mask
                id={animatedCircleMaskId}
                maskUnits="userSpaceOnUse"
                x="-64"
                y="-64"
                width="260"
                height="208"
              >
                <rect x="-64" y="-64" width="260" height="208" fill="white" />
                <path
                  d={PARALLELOGRAM_PATH}
                  fill="black"
                  className="control-exchange-logo-panel-mask-motion"
                />
              </mask>
              <mask
                id={animatedPanelMaskId}
                maskUnits="userSpaceOnUse"
                x="-64"
                y="-64"
                width="260"
                height="208"
              >
                <rect x="-64" y="-64" width="260" height="208" fill="white" />
                <circle
                  cx="42"
                  cy="40"
                  r="36"
                  fill="black"
                  className="control-exchange-logo-circle-mask-motion"
                />
              </mask>
            </>
          ) : (
            <>
              <clipPath id={clipId}>
                <circle cx="42" cy="40" r="36" />
              </clipPath>
              <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="132" height="80">
                <rect width="132" height="80" fill="white" />
                <path
                  d={PARALLELOGRAM_PATH}
                  fill="black"
                  clipPath={`url(#${clipId})`}
                />
              </mask>
            </>
          )}
        </defs>
        {animated ? (
          <>
            <circle
              cx="42"
              cy="40"
              r="36"
              mask={`url(#${animatedCircleMaskId})`}
              className={cn(circleClassName, "control-exchange-logo-circle-motion")}
            />
            <path
              d={PARALLELOGRAM_PATH}
              mask={`url(#${animatedPanelMaskId})`}
              className={cn(parallelogramClassName, "control-exchange-logo-panel-motion")}
            />
          </>
        ) : (
          <g mask={`url(#${maskId})`}>
            <circle cx="42" cy="40" r="36" className={circleClassName} />
            <path d={PARALLELOGRAM_PATH} className={parallelogramClassName} />
          </g>
        )}
      </svg>

      {!markOnly && (
        <span
          aria-hidden="true"
          className={cn(
            "whitespace-nowrap text-[1.05rem] font-semibold leading-none tracking-[-0.035em]",
            wordmarkClassName,
          )}
        >
          control exchange
        </span>
      )}
    </span>
  );
}
